<template>
  <div class="email-templates-manager">
    <div class="page-header">
      <div>
        <h1>{{ $t('bo.emailTemplates.title') }}</h1>
        <p>{{ $t('bo.emailTemplates.subtitle') }}</p>
      </div>
    </div>

    <div v-if="loading" class="loading">{{ $t('bo.emailTemplates.loading') }}</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>

    <div v-else class="template-groups">
      <section
        v-for="section in sections"
        :key="section.key"
        class="template-group"
      >
        <h2 class="group-title">
          {{ section.label }}
          <span class="group-count">{{ section.templates.length }}</span>
        </h2>
        <div class="templates-grid">
          <div
            v-for="tpl in section.templates"
            :key="tpl.template_key"
            class="template-card"
            :class="{ 'is-deprecated': isDeprecated(tpl) }"
          >
            <div class="card-header">
              <div class="card-title-row">
                <h3 class="card-title">{{ labelFor(tpl) }}</h3>
                <span class="key-badge">{{ tpl.template_key }}</span>
                <span
                  v-if="isDeprecated(tpl)"
                  class="badge-deprecated"
                  :title="$t('bo.emailTemplates.deprecatedHint')"
                >{{ $t('bo.emailTemplates.deprecated') }}</span>
              </div>
              <p class="card-desc">{{ descriptionFor(tpl) }}</p>
              <p v-if="isDeprecated(tpl)" class="card-deprecated-hint">{{ $t('bo.emailTemplates.deprecatedHint') }}</p>
            </div>

            <div class="card-languages">
              <div
                v-for="lang in tpl.languages"
                :key="lang.language"
                class="lang-row"
              >
                <div class="lang-info">
                  <span class="lang-flag">{{ flagFor(lang.language) }}</span>
                  <span class="lang-name">{{ langLabel(lang.language) }}</span>
                  <span v-if="lang.is_customized" class="badge-custom">{{ $t('bo.emailTemplates.customised') }}</span>
                  <span v-else-if="lang.has_hardcoded" class="badge-default">{{ $t('bo.emailTemplates.default') }}</span>
                  <span v-else class="badge-missing">{{ $t('bo.emailTemplates.notAvailable') }}</span>
                </div>
                <button
                  class="btn-edit"
                  @click="openEditor(tpl.template_key, lang.language)"
                >
                  {{ lang.is_customized ? $t('bo.common.edit') : $t('bo.common.create') }}
                </button>
              </div>
            </div>

            <div class="card-variables">
              <span class="vars-label">{{ $t('bo.emailTemplates.variables') }}</span>
              <span v-for="v in tpl.allowed_variables" :key="v" class="var-chip">
                &#123;&#123;{{ v }}&#125;&#125;
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Editor modal -->
    <EmailTemplateEditor
      v-if="editorOpen"
      :template-key="editorTemplateKey"
      :language="editorLanguage"
      :allowed-variables="currentAllowedVars"
      :label="currentLabel"
      @close="closeEditor"
      @saved="handleSaved"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import emailTemplateService from '@/services/emailTemplateService'
import EmailTemplateEditor from './EmailTemplateEditor.vue'

const { t, locale } = useI18n()

const templates    = ref([])
// Categories metier renvoyees par l'API (cle, libelle, libelles en/fr), dans l'ordre d'affichage.
const groups       = ref([])
const loading      = ref(false)
const error        = ref('')
const editorOpen   = ref(false)
const editorTemplateKey = ref('')
const editorLanguage    = ref('en')

const currentLabel = computed(() => {
  const tpl = templates.value.find(item => item.template_key === editorTemplateKey.value)
  return tpl ? labelFor(tpl) : ''
})

const currentAllowedVars = computed(() => {
  if (!editorTemplateKey.value) return []
  return templates.value.find(tpl => tpl.template_key === editorTemplateKey.value)?.allowed_variables ?? []
})

const LABELS = computed(() => ({
  INVITATION_USER:          t('bo.emailTemplates.labels.invitationUser'),
  INVITATION_ADMIN_TEAM:    t('bo.emailTemplates.labels.invitationAdminTeam'),
  INVITATION_START_JOURNEY: t('bo.emailTemplates.labels.invitationStartJourney'),
  WELCOME:                  t('bo.emailTemplates.labels.welcome'),
  OTP_VERIFICATION:         t('bo.emailTemplates.labels.otpVerification'),
  PASSWORD_RESET:           t('bo.emailTemplates.labels.passwordReset'),
  JOURNEY_STARTED:          t('bo.emailTemplates.labels.journeyStarted'),
  JOURNEY_CLOSED:           t('bo.emailTemplates.labels.journeyClosed'),
  FEEDBACK_REMINDER:        t('bo.emailTemplates.labels.feedbackReminder'),
  TRAINING_SPONSORSHIP_CREATED:  t('bo.emailTemplates.labels.sponsorshipCreated'),
  TRAINING_SPONSORSHIP_APPROVED: t('bo.emailTemplates.labels.sponsorshipApproved'),
  TRAINING_SPONSORSHIP_REJECTED: t('bo.emailTemplates.labels.sponsorshipRejected'),
}))

const DESCRIPTIONS = computed(() => ({
  INVITATION_USER:          t('bo.emailTemplates.descriptions.invitationUser'),
  INVITATION_ADMIN_TEAM:    t('bo.emailTemplates.descriptions.invitationAdminTeam'),
  INVITATION_START_JOURNEY: t('bo.emailTemplates.descriptions.invitationStartJourney'),
  WELCOME:                  t('bo.emailTemplates.descriptions.welcome'),
  OTP_VERIFICATION:         t('bo.emailTemplates.descriptions.otpVerification'),
  PASSWORD_RESET:           t('bo.emailTemplates.descriptions.passwordReset'),
  JOURNEY_STARTED:          t('bo.emailTemplates.descriptions.journeyStarted'),
  JOURNEY_CLOSED:           t('bo.emailTemplates.descriptions.journeyClosed'),
  FEEDBACK_REMINDER:        t('bo.emailTemplates.descriptions.feedbackReminder'),
  TRAINING_SPONSORSHIP_CREATED:  t('bo.emailTemplates.descriptions.sponsorshipCreated'),
  TRAINING_SPONSORSHIP_APPROVED: t('bo.emailTemplates.descriptions.sponsorshipApproved'),
  TRAINING_SPONSORSHIP_REJECTED: t('bo.emailTemplates.descriptions.sponsorshipRejected'),
}))

const LANG_LABELS = { en: 'English', fr: 'Français', es: 'Español', de: 'Deutsch' }
const LANG_FLAGS  = { en: '🇬🇧', fr: '🇫🇷', es: '🇪🇸', de: '🇩🇪' }

// Libelle / description : traduction locale du BO si elle existe, sinon ceux renvoyes par l'API
// (config/email_templates.php, en/fr/es/de), jamais la cle technique brute.
const currentLang    = () => String(locale.value || 'en').slice(0, 2)
const labelFor       = (tpl) => LABELS.value[tpl.template_key]
  ?? tpl.labels?.[currentLang()] ?? tpl.label ?? tpl.template_key
const descriptionFor = (tpl) => DESCRIPTIONS.value[tpl.template_key]
  ?? tpl.descriptions?.[currentLang()] ?? tpl.description ?? ''
// US BO 23 : template plus envoye par la plateforme, garde pour les personnalisations existantes.
const isDeprecated   = (tpl) => tpl.status === 'deprecated'
// Templates actifs d'abord, ordre de l'API conserve ; les "Not in use" en fin de liste.
const sortedTemplates = computed(() => [
  ...templates.value.filter(tpl => !isDeprecated(tpl)),
  ...templates.value.filter(tpl => isDeprecated(tpl)),
])
// Une section par categorie (ordre de l'API), titre dans la langue du BO ; categorie vide masquee.
// Un modele sans categorie connue (API plus ancienne) va dans "Autres" plutot que de disparaitre.
const groupLabel = (group) => group.labels?.[currentLang()] ?? group.label ?? group.key
const sections = computed(() => {
  const known = new Set(groups.value.map(group => group.key))
  const list = groups.value.map(group => ({
    key: group.key,
    label: groupLabel(group),
    templates: sortedTemplates.value.filter(tpl => tpl.group === group.key),
  }))
  const others = sortedTemplates.value.filter(tpl => !known.has(tpl.group))
  if (others.length) {
    list.push({ key: '__other', label: t('bo.emailTemplates.otherGroup'), templates: others })
  }
  return list.filter(section => section.templates.length > 0)
})
const langLabel      = (lang) => LANG_LABELS[lang] ?? lang
const flagFor        = (lang) => LANG_FLAGS[lang] ?? '🌐'

const loadTemplates = async () => {
  loading.value = true
  error.value   = ''
  try {
    const res = await emailTemplateService.list()
    templates.value = res.templates ?? []
    groups.value    = res.groups ?? []
  } catch {
    error.value = t('bo.emailTemplates.failedLoad')
  } finally {
    loading.value = false
  }
}

const openEditor = (templateKey, language) => {
  editorTemplateKey.value = templateKey
  editorLanguage.value    = language
  editorOpen.value        = true
}

const closeEditor = () => {
  editorOpen.value = false
}

const handleSaved = () => {
  editorOpen.value = false
  loadTemplates()
}

onMounted(loadTemplates)
</script>

<style scoped>
.email-templates-manager {
  padding: 2rem;
}

.page-header {
  margin-bottom: 2rem;
}

.page-header h1 {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.25rem;
}

.page-header p {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
}

.loading {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.error-msg {
  padding: 1rem;
  color: #dc2626;
  background: #fef2f2;
  border-radius: 0.5rem;
}

.template-groups {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.group-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.group-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 999px;
  padding: 0.1rem 0.5rem;
}

.templates-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.template-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  overflow: hidden;
}

.card-header {
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #f3f4f6;
}

.card-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.4rem;
}

.card-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.key-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 0.25rem;
  font-size: 0.72rem;
  font-family: monospace;
  font-weight: 600;
}

.card-desc {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
}

.card-languages {
  padding: 0.75rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.lang-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  background: #f9fafb;
}

.lang-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.lang-flag { font-size: 1.1rem; }

.lang-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.badge-custom {
  padding: 0.15rem 0.5rem;
  background: #d1fae5;
  color: #065f46;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
}

.badge-default {
  padding: 0.15rem 0.5rem;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
}

.template-card.is-deprecated {
  opacity: 0.75;
  border-style: dashed;
}

.badge-deprecated {
  padding: 0.15rem 0.5rem;
  background: #fef3c7;
  color: #92400e;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
}

.card-deprecated-hint {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: #92400e;
}

.badge-missing {
  padding: 0.15rem 0.5rem;
  background: #f3f4f6;
  color: #9ca3af;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
}

.btn-edit {
  padding: 0.3rem 0.75rem;
  border: 1px solid #3b82f6;
  color: #3b82f6;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: background 0.15s;
}

.btn-edit:hover { background: #eff6ff; }

.card-variables {
  padding: 0.75rem 1.5rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
}

.vars-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.var-chip {
  padding: 0.15rem 0.45rem;
  background: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
  border-radius: 0.25rem;
  font-size: 0.72rem;
  font-family: monospace;
}
</style>
